import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Component } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import MarketingPage from "./pages/MarketingPage";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import ProfileSetup from "./pages/ProfileSetup";
import DiscoverPage from "./pages/DiscoverPage";
import MatchesPage from "./pages/MatchesPage";
import ChatPage from "./pages/ChatPage";
import AdminDashboard from "./pages/AdminDashboard";
import CountdownPage from "./pages/CountdownPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import "./App.css";
import { usePushNotifications } from './hooks/usePushNotifications';

// ── Error Boundary ────────────────────────────────────────────────
// tweddi: cream ground, Inclusive Sans, ink. The monogram is INLINE SVG,
// not a remote image — if the app crashed because the network died, a
// hosted logo would fail to load and the error screen would look broken.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const INK = '#141313';
      const CREAM = '#FFFDF8';
      const MUTE = '#6E6B62';
      return (
        <div style={{
          minHeight: '100dvh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          padding: '2rem', background: CREAM, maxWidth: '28rem', margin: '0 auto',
          fontFamily: "'Inclusive Sans', system-ui, sans-serif",
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: '2.5rem' }}>
            <svg viewBox="-60 -60 664 520" width="30" height={30 * (520 / 664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0 }} aria-label="tweddi">
              <circle cx="200" cy="200" r="108" fill="none" stroke={INK} strokeWidth="66" />
              <circle cx="344" cy="200" r="108" fill="none" stroke={CREAM} strokeWidth="108" />
              <circle cx="344" cy="200" r="108" fill="none" stroke={INK} strokeWidth="66" />
              <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={CREAM} strokeWidth="108" />
              <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={INK} strokeWidth="66" />
            </svg>
            <span style={{ fontSize: '1.3rem', fontWeight: 600, letterSpacing: '-0.01em', color: INK }}>tweddi</span>
          </div>
          <p style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.75rem' }}>Something went wrong</p>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 600, color: INK, margin: '0 0 0.75rem', letterSpacing: '-0.03em', lineHeight: 1.05 }}>Unexpected error.</h1>
          <p style={{ fontSize: '0.8rem', fontWeight: 500, color: MUTE, margin: '0 0 2.5rem', lineHeight: 1.6 }}>Something didn't go as planned. Tap below to reload the app.</p>
          <button onClick={() => window.location.reload()} style={{ width: '100%', height: '3.25rem', background: INK, color: CREAM, border: 'none', borderRadius: '9999px', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer' }}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Route guards ──────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-bone">
      <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};
const ProfileRequired = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.profile_complete && !user.is_host) return <Navigate to="/profile-setup" replace />;
  return children;
};
const EventRequired = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.event_id) return <Navigate to="/join" replace />;
  return children;
};

// ── Root ──────────────────────────────────────────────────────────
// "/" is the Landing page: the logo, the code box, and the couple's path.
// Marketing (the pitch) lives at /discover-tweddi for people who want to
// understand what tweddi is before they have a code.
//
// If an invite link carries a code (tweddi.com/?code=ABC123), stash it so
// Landing can look the wedding up immediately and skip straight to the
// confirmation card.
const CODE_RE = /^[A-Z0-9]{4,10}$/;

const RootGate = () => {
  const [params] = useSearchParams();
  const raw = params.get('code') || params.get('c') || params.get('event');
  const code = raw ? raw.trim().toUpperCase() : null;

  if (code && CODE_RE.test(code)) {
    try { localStorage.setItem('pendingEventCode', code); } catch {}
  }
  return <LandingPage />;
};

// ── Routes ────────────────────────────────────────────────────────
function AppRoutes() {
  const { user } = useAuth();
  usePushNotifications(user);
  const getAuthRedirect = () => {
    if (!user) return null;
    if (user.is_host) return "/admin";
    if (!user.event_id) return "/join";
    if (!user.profile_complete) return "/profile-setup";
    return "/discover";
  };
  const authRedirect = getAuthRedirect();
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/discover-tweddi" element={<MarketingPage />} />
      <Route path="/join" element={<LandingPage />} />
      <Route path="/countdown/:eventCode" element={<CountdownPage />} />
      <Route path="/auth" element={user ? <Navigate to={authRedirect} replace /> : <AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><EventRequired><ProfileRequired><DiscoverPage /></ProfileRequired></EventRequired></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><EventRequired><ProfileRequired><MatchesPage /></ProfileRequired></EventRequired></ProtectedRoute>} />
      <Route path="/chat/:matchId" element={<ProtectedRoute><EventRequired><ProfileRequired><ChatPage /></ProfileRequired></EventRequired></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute>{user?.is_host ? <AdminDashboard /> : <Navigate to="/join" replace />}</ProtectedRoute>} />
    </Routes>
  );
}

// ── App ───────────────────────────────────────────────────────────
function App() {
  return (
    <ErrorBoundary>
      <div className="app-container paper-texture">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}
export default App;
